import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Cpu,
  Lock,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import SEO from '@/components/common/SEO';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import CorporateFooter from '@/components/layout/CorporateFooter';
import Header from '@/components/layout/Header';
import PricingSection from '@/components/layout/PricingSection';
import SovereignComputeStats from '@/components/landing/SovereignComputeStats';
import SelfServePricing from '@/components/landing/SelfServePricing';
import { NewsItem } from '@/types';
import {
  capabilityHighlights,
  enterpriseHero,
  sovereigntyHighlights,
  supportedSectors,
} from '@/lib/marketing';
import { featuredSeoPages, pageLabels } from '@/lib/seo-pages';
import { modelTrainingServiceSchema, softwareApplicationSchema, toSlug } from '@/lib/seo';

const stripHtml = (html: string): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const capabilityIcons = [Lock, ShieldCheck, Cpu, Building2];

const LandingPage = () => {
  const [featuredNews, setFeaturedNews] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [mainArticles, setMainArticles] = useState<NewsItem[]>([]);
  const [mainArticlesLoading, setMainArticlesLoading] = useState(true);
  const pricingSectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedNews = async () => {
      setNewsLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('is_featured', true)
        .order('date', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching featured news:', error);
      } else {
        setFeaturedNews(data || []);
      }

      setNewsLoading(false);
    };

    const fetchMainArticles = async () => {
      setMainArticlesLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('is_main_featured_article', true)
        .order('date', { ascending: false })
        .limit(3);

      if (error) {
        console.error('Error fetching main featured articles:', error);
      } else {
        setMainArticles(data || []);
      }

      setMainArticlesLoading(false);
    };

    fetchFeaturedNews();
    fetchMainArticles();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="CanXP AI — Sovereign Canadian AI Platform for Private Models and Enterprise Intelligence"
        description="CanXP AI helps Canadian organizations deploy private AI systems, train small language models, manage secure knowledge bases, and operate AI under Canadian jurisdiction."
        keywords="sovereign AI Canada, Canadian AI platform, private AI for business, AI model training, small language model training, Canadian AI infrastructure"
        url="/"
        schema={[softwareApplicationSchema, modelTrainingServiceSchema]}
      />

      <Header />

      <main>
        <section className="relative overflow-hidden px-4 pb-20 pt-20 md:pb-28 md:pt-28">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(220,38,38,0.16),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(220,38,38,0.12),transparent_30%)]" />
          <div className="container mx-auto max-w-6xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mx-auto max-w-4xl"
            >
              <div className="mb-6 flex justify-center">
                <div className="rounded-full border border-primary/20 bg-background/70 p-4 shadow-[0_0_80px_-28px_rgba(220,38,38,0.8)] backdrop-blur">
                  <MapleLeaf size={54} className="text-canada-red" />
                </div>
              </div>

              <Badge variant="outline" className="border-primary/25 bg-background/70 px-4 py-1 text-primary backdrop-blur">
                {enterpriseHero.badge}
              </Badge>
              <h1 className="mt-6 text-5xl font-semibold tracking-tight md:text-7xl">
                Build sovereign AI systems that you control.
              </h1>
              <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-muted-foreground md:text-xl">
                Design, train, deploy, and host AI for governments, enterprises, healthcare, industry, and institutional teams that need privacy and control.
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {supportedSectors.map((sector) => (
                  <span
                    key={sector}
                    className="rounded-full border border-border/70 bg-background/70 px-4 py-2 text-sm font-medium text-foreground/85 backdrop-blur"
                  >
                    {sector}
                  </span>
                ))}
              </div>

              <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild size="lg" className="gap-2">
                  <Link to={enterpriseHero.primaryCtaHref}>
                    {enterpriseHero.primaryCtaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  onClick={() => pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  {enterpriseHero.secondaryCtaLabel}
                </Button>
              </div>

              <div className="mt-4 flex justify-center">
                <Button asChild variant="ghost" className="gap-2 text-primary hover:bg-transparent hover:text-primary dark:hover:bg-transparent dark:hover:text-white">
                  <Link to="/insights">
                    Explore Sovereign AI Insights
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

            </motion.div>
          </div>
        </section>

        <SovereignComputeStats />

        <SelfServePricing />

        <section className="px-4 py-6">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Enterprise capability
              </Badge>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
                Engineering for those that care about ownership, performance, and control.
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                CanXP AI is not a generic AI wrapper. We build the model, deployment, and infrastructure layer required
                for serious institutional use.
              </p>
            </motion.div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {capabilityHighlights.map((highlight, index) => {
                const Icon = capabilityIcons[index];

                return (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.1 * index }}
                  >
                    <Card className="h-full border-border/70 bg-background/80 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.6)] backdrop-blur">
                      <CardContent className="flex h-full flex-col p-6">
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">
                          {highlight.eyebrow}
                        </p>
                        <h3 className="mt-3 text-2xl font-semibold tracking-tight">{highlight.title}</h3>
                        <p className="mt-4 text-sm leading-7 text-muted-foreground">{highlight.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="sovereignty" className="px-4 py-20">
          <div className="container mx-auto max-w-7xl rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-8 shadow-[0_24px_120px_-72px_rgba(220,38,38,0.9)] md:p-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                Sovereignty and control
              </Badge>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
                Sovereign AI is an operating decision, not a branding exercise.
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                Institutions need AI infrastructure that respects jurisdiction, supports compliance, and keeps critical
                capability under their control as deployment scales.
              </p>
            </motion.div>

            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
              {sovereigntyHighlights.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.08 * index }}
                >
                  <Card className="h-full border-primary/15 bg-background/80 backdrop-blur">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="mt-1 rounded-full border border-primary/20 bg-primary/10 p-2 text-primary">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold tracking-tight">{item.title}</h3>
                          <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Core pillars
              </Badge>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
                Explore the core of CanXP AI.
              </h2>
              <p className="mt-5 text-lg leading-8 text-muted-foreground">
                CanXP AI is building a stronger semantic architecture around sovereign AI, private AI, model training,
                Canadian infrastructure, and sector-specific deployment.
              </p>
            </motion.div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {featuredSeoPages.map((page, index) => (
                <motion.div
                  key={page.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.08 * index }}
                >
                  <Card className="h-full border-border/70 bg-background/80 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.5)]">
                    <CardContent className="flex h-full flex-col p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">{page.badge}</p>
                      <h3 className="mt-3 text-2xl font-semibold tracking-tight">{pageLabels[page.path]}</h3>
                      <p className="mt-4 flex-1 text-sm leading-7 text-muted-foreground">{page.description}</p>
                      <Button asChild variant="link" className="mt-4 px-0">
                        <Link to={page.path}>
                          Read the page
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section ref={pricingSectionRef}>
          <PricingSection />
        </section>

        <section className="px-4 py-20">
          <div className="container mx-auto max-w-6xl rounded-[2rem] border border-border/70 bg-background/80 p-8 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.5)] md:p-12">
            <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div>
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  Discuss your deployment
                </Badge>
                <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
                  Bring us the mandate, the constraint set, and the deployment target.
                </h2>
                <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">
                  We work with governments, healthcare groups, universities, industrial firms, and enterprise teams that
                  need sovereign architecture, private deployment, or model engineering support.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="h-auto w-full gap-2 whitespace-normal py-4 text-center sm:w-auto sm:whitespace-nowrap">
                    <Link to="/enterprise">

                      <span className="break-words">Request a Sovereign AI Architecture Review</span>
                      <ArrowRight className="h-4 w-4 shrink-0" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-auto w-full whitespace-normal py-4 text-center sm:w-auto sm:whitespace-nowrap">
                    <Link to="/purchase?service=starter-fine-tuning">
                      <span className="break-words">Explore Direct-Purchase Services</span>
                    </Link>
                  </Button>
                </div>

              </div>

            </div>
          </div>
        </section>

        {!newsLoading && featuredNews.length > 0 ? (
          <section className="hidden px-4 py-8 md:block">
            <motion.div
              className="container mx-auto max-w-7xl overflow-hidden"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >

              <div className="mx-auto mb-10 max-w-3xl text-center">
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  Latest from CanXP AI
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">Briefings, launches, and field updates.</h2>
              </div>
              <Carousel opts={{ align: 'start', loop: true }} className="w-full">
                <CarouselContent>
                  {featuredNews.map((item) => (
                    <CarouselItem key={item.id} className="basis-[calc(100%-1rem)] md:basis-1/2 lg:basis-1/3">
                      <div className="p-1 h-full">
                        <Card
                          className="group flex h-full cursor-pointer flex-col overflow-hidden border-border/70 text-left shadow-md transition-shadow duration-300 hover:shadow-xl"
                          onClick={() => navigate(`/news/${item.id}/${toSlug(item.title)}`)}
                        >

                          <CardContent className="flex flex-grow flex-col p-0">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src={item.image_url || '/placeholder.svg'}
                                alt={item.title}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            </div>
                            <div className="flex flex-grow flex-col p-6">
                              <h3 className="flex-grow text-xl font-semibold leading-tight transition-colors group-hover:text-primary">
                                {item.title}
                              </h3>
                              <div className="mt-4 border-t pt-4 text-xs text-muted-foreground">
                                {new Date(item.date).toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </motion.div>
          </section>
        ) : null}

        {!mainArticlesLoading && mainArticles.length > 0 ? (
          <section className="px-4 py-20">
            <motion.div
              className="container mx-auto max-w-7xl"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="mx-auto mb-12 max-w-3xl text-center">
                <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                  Executive perspective
                </Badge>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">CanXP AI articles and analysis.</h2>
              </div>
              <div className="space-y-12">
                {mainArticles.map((article) => (
                  <Card
                    key={article.id}
                    className="group grid cursor-pointer grid-cols-1 items-center overflow-hidden border-border/70 shadow-lg transition-shadow duration-300 hover:shadow-xl md:grid-cols-2"
                    onClick={() => navigate(`/news/${article.id}/${toSlug(article.title)}`)}
                  >

                    <div className="aspect-video overflow-hidden md:h-full md:aspect-auto">
                      <img
                        src={article.image_url || '/placeholder.svg'}
                        alt={article.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>

                    <CardContent className="flex flex-col p-8 md:p-12">
                      <h3 className="text-2xl font-semibold leading-tight transition-colors group-hover:text-primary md:text-3xl">
                        {article.title}
                      </h3>
                      <p className="mt-4 flex-grow text-muted-foreground">
                        {`${stripHtml(article.content).substring(0, 250)}...`}
                      </p>
                      <div className="mt-6 flex items-center justify-between gap-4">
                        <Button variant="link" className="h-auto p-0 font-semibold text-primary">
                          Read full article <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {new Date(article.date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>
        ) : null}
      </main>

      <CorporateFooter />
    </div>
  );
};

export default LandingPage;
